<template>
  <LayoutPage :title="pageTitle" :loading="smartZonesLoading">
    <template v-slot:title>
      <span v-if="facilityFeatureEnabled('video_camera_integration')">
        <span
          style="color: rgb(255, 65, 54); font-weight: bold"
          class="headline"
          >+Visual</span
        >
        <span
          style="font-weight: bold"
          class="headline"
          :style="
            $vuetify.theme.dark ? 'color: white' : 'color: rgb(4, 43, 93)'
          "
          >Verify Smart Zones</span
        >
      </span>
      <span v-else class="headline"> Smart Zones </span>
    </template>
    <template #actions>
      <div class="mr-4" style="min-width: 240px">
        <SearchField v-model="search" />
      </div>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" class="action-btn" v-bind="attrs" v-on="on">
            Add Zone <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="navigateToAddZone">
            <v-list-item-title>Add a Zone</v-list-item-title>
          </v-list-item>
          <v-list-item @click="showBulkUploadModal = true">
            <v-list-item-title>Bulk Upload Zones</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <FeatureWrapper feature="video_camera_integration">
      <VisualVerifyGrid
        :itemsPerPageProp="8"
        displayMode="compact"
        @tab-changed="handleTabChange"
      />
      <!-- Stats tiles below camera grid for VV+ facilities -->
      <v-row v-if="!zoneStatsLoading" class="mb-2 mt-2">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold">{{ zoneStats.zones }}</div>
              <div class="caption grey--text">Total Zones</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold">{{ zoneStats.total }}</div>
              <div class="caption grey--text">Total Units</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold green--text">
                {{ zoneStats.mapped }}
              </div>
              <div class="caption grey--text">Mapped Units</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div
                class="text-h5 font-weight-bold"
                :class="zoneStats.unmapped > 0 ? 'red--text' : 'grey--text'"
              >
                {{ zoneStats.unmapped }}
              </div>
              <div class="caption grey--text">Unmapped Units</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </FeatureWrapper>

    <!-- Stats tiles at top for non-VV+ facilities -->
    <template v-if="!facilityFeatureEnabled('video_camera_integration')">
      <v-row v-if="!zoneStatsLoading" class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold">{{ zoneStats.zones }}</div>
              <div class="caption grey--text">Total Zones</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold">{{ zoneStats.total }}</div>
              <div class="caption grey--text">Total Units</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 font-weight-bold green--text">
                {{ zoneStats.mapped }}
              </div>
              <div class="caption grey--text">Mapped Units</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div
                class="text-h5 font-weight-bold"
                :class="zoneStats.unmapped > 0 ? 'red--text' : 'grey--text'"
              >
                {{ zoneStats.unmapped }}
              </div>
              <div class="caption grey--text">Unmapped Units</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-data-table
      v-show="!isMonitorTabActive"
      :headers="headers"
      :items="zones"
      :loading="smartZonesLoading"
      loading-text="Loading Smart Zones..."
      :search="search"
      :custom-filter="customFilter"
      :custom-sort="customSort"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 500] }"
      :sort-by="['name']"
    >
      <template #item.actions="{ item }">
        <ButtonView @click="gotoZone(item.id)" />
      </template>

      <template
        #item.activity_status="{ item: { devices, incidents, profile } }"
      >
        <ZoneStatusCluster
          :profile="profile"
          :devices="devices"
          :incidents="incidents"
          small
        />
      </template>
      <template #item.environmental_status="{ item: { devices, profile } }">
        <div v-if="hasClimateEnabled(profile)">
          <ZoneClimateCluster :profile="profile" :devices="devices" small />
        </div>
        <div v-else class="grey--text">N/A</div>
      </template>
      <template #item.video_status="{ item: { devices, profile } }">
        <div v-if="hasVideoEnabled(devices)">
          <VideoViewer
            small
            :video-config="profile.videoConfig"
            :devices="devices"
          />
        </div>
        <div v-else class="grey--text">
          <v-btn small depressed color="transparent">
            <v-icon color="grey" small left> fas fa-play-circle </v-icon>
            View Video
          </v-btn>
        </div>
      </template>
      <template #item.last_incident_type="{ item }">
        <div v-if="item && item.lastIncident && item.lastIncident[0]">
          <IconIncidentType :incident="item.lastIncident[0]" />
          {{ formatToIncidentType(item.lastIncident[0]) }}
        </div>
        <div v-else class="grey--text">N/A</div>
      </template>
      <template #item.last_incident_date="{ item }">
        <div
          v-if="
            item &&
            item.lastIncident &&
            item.lastIncident[0] &&
            item.lastIncident[0].startedAt
          "
        >
          {{ formatToIncidentDateTime(item.lastIncident[0].startedAt) }}
        </div>
        <div v-else class="grey--text">N/A</div>
      </template>
      <template #item.units_count="{ item }">
        <span>{{ getZoneUnitCount(item) }}</span>
      </template>
      <template #item.camera_name="{ item }">
        <span v-if="getZoneCameraName(item)">
          {{ getZoneCameraName(item) }}
        </span>
        <span v-else class="grey--text">N/A</span>
      </template>
    </v-data-table>

    <BulkUploadZoneModal
      v-if="showBulkUploadModal"
      :facility-id="currentFacilityId"
      mode="zone"
      @close="showBulkUploadModal = false"
    />
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import SearchField from "@atoms/SearchField.vue";
import VideoViewer from "../fixtures/VideoViewer.vue";
import ButtonView from "@atoms/ButtonView.vue";
import ZoneClimateCluster from "../fixtures/ZoneClimateCluster.vue";
import ZoneStatusCluster from "../fixtures/ZoneStatusCluster.vue";
import { organizationSmartZones } from "../../graphql/organizations";
import useFacility from "../authentication/useFacility";
import { formatIncidentType } from "@components/common/helpers/incident";
import { formatIncidentDateTime } from "@tod-ui/helpers/datetime";
import IconIncidentType from "@atoms/IconIncidentType.vue";
import VisualVerifyGrid from "./CardVisualVerifyGrid.vue";
import FeatureWrapper from "@layout/FeatureWrapper.vue";
import { FACILITY_ZONE_STATS } from "./graphql";
import BulkUploadZoneModal from "../manage_facility/BulkUploadZoneModal.vue";

export default {
  name: "PageSmartZones",
  components: {
    SearchField,
    VideoViewer,
    ButtonView,
    ZoneClimateCluster,
    ZoneStatusCluster,
    LayoutPage,
    IconIncidentType,
    VisualVerifyGrid,
    FeatureWrapper,
    BulkUploadZoneModal,
  },
  setup() {
    const { currentFacilityId, facilityTimeZone, facilityFeatureEnabled } =
      useFacility();
    return {
      currentFacilityId,
      facilityTimeZone,
      facilityFeatureEnabled,
    };
  },
  data: () => ({
    smartZones: [],
    activeTabIndex: 0,
    search: "",
    showBulkUploadModal: false,
    facilityZoneStats: null,
    headers: [
      {
        text: "Details",
        align: "start",
        filterable: false,
        sortable: false,
        value: "actions",
        width: "5%",
      },
      {
        text: "Smart Zone",
        value: "name",
        width: "15%",
      },
      {
        text: "Units",
        filterable: false,
        sortable: false,
        value: "units_count",
        width: "8%",
      },
      {
        text: "Camera",
        filterable: false,
        sortable: false,
        value: "camera_name",
        width: "10%",
      },
      {
        text: "Activity Status",
        filterable: false,
        sortable: false,
        value: "activity_status",
        width: "12%",
      },
      {
        text: "Environmental Status",
        filterable: false,
        sortable: false,
        value: "environmental_status",
        width: "12%",
      },
      {
        text: "Video Status",
        filterable: false,
        sortable: false,
        value: "video_status",
        width: "12%",
      },
      {
        text: "Last Incident Type",
        filterable: false,
        sortable: false,
        value: "last_incident_type",
        width: "12%",
      },
      {
        text: "Last Incident (Date, Time)",
        filterable: false,
        sortable: false,
        value: "last_incident_date",
        width: "14%",
      },
    ],
  }),
  computed: {
    pageTitle() {
      return this.facilityFeatureEnabled("video_camera_integration")
        ? "VisualVerify Smart Zones"
        : "Smart Zones";
    },
    smartZonesLoading() {
      return this.$apollo.queries.smartZones.loading;
    },
    zoneStatsLoading() {
      return this.$apollo.queries.facilityZoneStats
        ? this.$apollo.queries.facilityZoneStats.loading
        : true;
    },
    zoneStats() {
      return (
        this.facilityZoneStats || { total: 0, zones: 0, mapped: 0, unmapped: 0 }
      );
    },
    isMonitorTabActive() {
      return this.activeTabIndex === 1;
    },

    zones() {
      if (this.smartZonesLoading) return [];

      return this.smartZones.map((zone) => {
        return {
          ...zone,
        };
      });
    },
  },
  apollo: {
    smartZones: {
      query: organizationSmartZones,
      variables() {
        return {
          id: this.currentFacilityId,
          incident_statuses: ["started", "muted"],
          incident_resolution: null,
        };
      },
      // don't cache so we get the most real-time values for climate, etc.
      fetchPolicy: "network-only",
      pollInterval: 10000,
      update: ({ smartZones }) => smartZones,
    },
    facilityZoneStats: {
      query: FACILITY_ZONE_STATS,
      variables() {
        return { facilityId: this.currentFacilityId };
      },
      update({ facilityZoneStats }) {
        return facilityZoneStats;
      },
      skip() {
        return !this.currentFacilityId;
      },
    },
  },
  methods: {
    navigateToAddZone() {
      this.$router.push({ name: "AddSmartUnit", query: { subtype: "zone" } });
    },
    customFilter(value, search, item) {
      if (!search) return true;
      // NOTE: item is an `organization`
      let _search = search.toLowerCase();

      // Filter on `name`
      const name = `${item.name}`;
      if (name.toLowerCase().indexOf(_search) >= 0) return true;
    },
    customSort(items, sortBy, sortDesc) {
      // sortBy and sortDesc are arrays
      const _sortBy = sortBy[0];
      const _sortDesc = sortDesc[0];

      items.sort((a, b) => {
        if (_sortBy === "name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          if (!_sortDesc) return aName < bName ? -1 : 1;
          return bName < aName ? -1 : 1;
        } else {
          // Default sort comparison
          if (!_sortDesc) return a[_sortBy] < b[_sortBy] ? -1 : 1;
          return b[_sortBy] < a[_sortBy] ? -1 : 1;
        }
      });

      return items;
    },

    // Climate Config
    hasClimateEnabled({ climateConfig }) {
      return climateConfig && !climateConfig.disabled;
    },
    hasClimateDisabled({ climateConfig }) {
      return climateConfig?.disabled;
    },
    // Door/Entry Config
    hasEntryEnabled({ entryConfig }) {
      return entryConfig && !entryConfig.disabled;
    },
    hasEntryDisabled({ entryConfig }) {
      return entryConfig?.disabled;
    },
    // Motion Config
    hasMotionEnabled({ motionConfigs }) {
      const motion = motionConfigs?.find((c) => c.mode === "motion");
      return motion && !motion.disabled;
    },
    hasMotionDisabled({ motionConfigs }) {
      const motion = motionConfigs?.find((c) => c.mode === "motion");
      return motion?.disabled;
    },

    // Video Config
    hasVideoEnabled(devices) {
      if (!devices || devices.length === 0) {
        return false;
      }
      return devices.find(
        (device) => device.type === "een" && device.subtype === "video_camera"
      );
    },

    getZoneUnitCount(zone) {
      // descendants_count may be available from the query; fallback to 0
      return zone.descendants_count ?? zone.descendantsCount ?? "—";
    },

    getZoneCameraName(zone) {
      const camera =
        zone.devices &&
        zone.devices.find(
          (d) => d.type === "een" && d.subtype === "video_camera"
        );
      // Show the device short id (e.g. VC100A0AED), not the display name.
      if (camera) return camera.shortId || camera.externalId;
      return null;
    },

    // navigational methods
    gotoZone(zoneId) {
      this.$router.push({
        name: "ViewSmartZone",
        params: {
          smart_zone_id: zoneId,
        },
      });
    },
    formatToIncidentType(incident) {
      return formatIncidentType(incident);
    },
    formatToIncidentDateTime(startedAt) {
      return formatIncidentDateTime(startedAt, this.facilityTimeZone);
    },
    handleTabChange(tabIndex) {
      this.activeTabIndex = tabIndex;
    },
  },
};
</script>
